'use client'

import { format, isValid, parseISO } from 'date-fns'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface DatePickerProps
  extends Omit<React.ComponentPropsWithoutRef<'button'>, 'type' | 'children'> {
  value: string
  onValueChange?: (value: string) => void
  placeholder?: string
}

function formatIsoDate(date: Date) {
  return format(date, 'yyyy-MM-dd')
}

function parseIsoDate(value: string) {
  const date = parseISO(value)
  return isValid(date) ? date : undefined
}

function formatDisplayDate(value?: Date) {
  return value ? format(value, 'PPP') : 'Pick a date'
}

function DatePicker({
  className,
  value,
  onValueChange,
  placeholder,
  id,
  name,
  disabled,
  ...props
}: DatePickerProps) {
  const selectedDate = React.useMemo(() => parseIsoDate(value), [value])

  const handleSelect = (nextDate: Date | undefined) => {
    onValueChange?.(nextDate ? formatIsoDate(nextDate) : '')
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={id}
          name={name}
          disabled={disabled}
          variant="outline"
          className={cn(
            'bg-background',
            'border',
            'border-input',
            'justify-between',
            'px-3',
            'py-2',
            'rounded-md',
            'shadow-xs',
            'text-sm',
            'text-left',
            'disabled:cursor-not-allowed',
            'disabled:opacity-50',
            'disabled:pointer-events-none',
            'transition-colors',
            'hover:bg-accent/5',
            className,
          )}
          {...props}
        >
          {selectedDate
            ? formatDisplayDate(selectedDate)
            : (placeholder ?? 'Pick a date')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  )
}

export { DatePicker }
